import Head from 'next/head';

import { Box, Flex, Link, Text } from '@chakra-ui/react';

import { isNil } from 'lodash';

import { commonHelpers } from '@nocowanie/core';
import { IconsLinear } from '@nocowanie/icons';

import { BreadcrumbsProps } from './breadcrumbs.props';
import { generateBreadcrumbSchema } from './breadcrumbs.utils';
import { useHorizontalScroll } from './useHorizontalScroll';

export const BreadCrumbs = ({ breadcrumbs, name, baseUrl }: BreadcrumbsProps) => {
    const { ref: breadcrumbsRef, canScrollLeft, canScrollRight } = useHorizontalScroll();

    if (!breadcrumbs?.length || isNil(name)) return null;

    const breadcrumbsShadowCommonStyles = {
        position: 'absolute' as const,
        top: 0,
        width: 8,
        height: '100%',
    };

    const schemaObj = generateBreadcrumbSchema(breadcrumbs, name, baseUrl);

    return (
        <>
            <Head>
                <script
                    type="application/ld+json"
                    dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaObj) }}
                />
            </Head>
            <Box position="relative" mt={4}>
                {canScrollLeft && (
                    <Box
                        {...breadcrumbsShadowCommonStyles}
                        left={0}
                        backgroundImage={
                            'linear-gradient(to left, rgba(255, 255, 255, 0), rgb(255, 255, 255))'
                        }
                    />
                )}
                <Flex
                    ref={breadcrumbsRef}
                    align="center"
                    gap={2}
                    scrollbar="hidden"
                    overflowX="auto"
                    whiteSpace="nowrap"
                >
                    {commonHelpers.intersperse(
                        breadcrumbs.map(crumb => (
                            <Link
                                display={'flex'}
                                key={crumb.position}
                                color="gray.500"
                                fontSize="sm"
                                href={crumb.item}
                                focusRing={'none'}
                                variant={'plain'}
                            >
                                {crumb.name}
                            </Link>
                        )),
                        n => (
                            <IconsLinear.ArrowRight2 key={`chevron-${n}`} size={'2xs'} mt={-0.5} />
                        ),
                    )}
                    <IconsLinear.ArrowRight2 size={'2xs'} mt={-0.5} />
                    <Text as={'strong'} color="gray.500" fontSize="sm" fontWeight={'normal'}>
                        {name}
                    </Text>
                </Flex>
                {canScrollRight && (
                    <Box
                        {...breadcrumbsShadowCommonStyles}
                        right={0}
                        backgroundImage={
                            'linear-gradient(to right, rgba(255, 255, 255, 0), rgb(255, 255, 255))'
                        }
                    />
                )}
            </Box>
        </>
    );
};
